import React from "react";
import { useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { enReportServiceType } from "../seviceBuilder/helper";
import { renderAttributeValue } from "../common/attributeUtils";
import { RootStateServiceLogicBuilder } from "../../store/service-logic-builder-slice";
import { Box } from "@mui/material";
import calendarIcon from "../../../new-reports/assets/calendarIcon.png";
import LinkSvg from "../../../../common/svgs/LinkSvg";
import { Link } from "react-router-dom";
import EditSvg from "../../../../common/svgs/EditSvg";
import ServiceInformationDetails from "../common/ServiceInformationDetails";
import PrescriptionRationale from "./PrescriptionRationale";
import {
  PrescribedServiceStatus,
  effectiveEndDate,
  formatPrescriptionPeriod,
  prescribedServiceStatus,
} from "../common/prescriptionPeriod";

interface Service {
  name: string;
  [key: string]: any;
}

interface PrescriptionServiceCardProps {
  service: Service;
  index: number;
  onEditService: (service: Service) => void;
  /** Hide the edit pencil — History's read-only scopes. */
  readOnly?: boolean;
}

interface DetailElementProps {
  label: string;
  value: string | number | object;
  isExpired?: boolean;
}

const DetailElement: React.FC<DetailElementProps> = ({ label, value }) => {
  const theme: Theme = useTheme();

  return (
    <div style={styles.detailElement}>
      <span style={styles.detailLabel(theme)}>{label}</span>
      <div style={styles.detailValueContainer}>
        <span style={styles.detailValue(theme)}>
          {typeof value === "object" ? JSON.stringify(value) : String(value)}
        </span>
      </div>
    </div>
  );
};

const STATUS_BADGE: Record<
  PrescribedServiceStatus,
  {
    label: string;
    background: (theme: Theme) => string;
    border: (theme: Theme) => string;
  }
> = {
  active: {
    label: "Active",
    background: (theme) => theme.colorTokens.S[10],
    border: (theme) => theme.colorTokens.S[70],
  },
  expired: {
    label: "Expired",
    background: (theme) => theme.colorTokens.D[10],
    border: (theme) => theme.colorTokens.D[70],
  },
  inactive: {
    label: "Inactive",
    background: (theme) => theme.colorTokens.N[20],
    border: (theme) => theme.colorTokens.N[70],
  },
};

const PrescriptionServiceCard: React.FC<PrescriptionServiceCardProps> = ({
  service,
  index,
  onEditService,
  readOnly = false,
}) => {
  const theme: Theme = useTheme();
  const savedServices = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.savedServices
  );

  // The image and description live on the affiliate's saved service. The
  // history detail carries them on the prescribed service itself; the Redux
  // catalog (loaded only inside the client workspace) is the fallback for the
  // prescription-builder payload, which doesn't. Missing both, the card still
  // renders — a history record must not vanish because its template was
  // deleted since.
  const savedService = savedServices.find(
    (savedSvc: any) => savedSvc.id === service.userServiceID
  );
  const imageURL: string | undefined =
    service.imageURL || savedService?.imageURL || undefined;
  const description: string | undefined =
    service.description || savedService?.description || undefined;

  const rules = service.rules;
  const status = prescribedServiceStatus({
    active: service.active,
    endDate: service.endDate,
    deactivationDate: service.deactivationDate,
  });

  return (
    <div key={service.name || index} style={styles.serviceCard(theme)}>
      <div style={styles.contentContainerHeader}>
        {imageURL && (
          <div style={styles.serviceImage}>
            <img
              key={service.id}
              src={imageURL}
              alt={service.name}
              style={{
                width: "100%",
              }}
            />
          </div>
        )}
        <div style={styles.serviceTitleContainer}>
          <div style={styles.serviceTitleWrapper}>
            <div style={styles.serviceTitle(theme)}>{service.name}</div>
            <div
              style={{
                ...styles.serviceBadge(theme),
                backgroundColor: STATUS_BADGE[status].background(theme),
                border: `1px solid ${STATUS_BADGE[status].border(theme)}`,
              }}
            >
              {STATUS_BADGE[status].label}
            </div>
            {/* Only a running service can be edited: the pencil follows the
                status badge, so an Expired or Inactive service shows none. */}
            {!readOnly && status === "active" && (
              <div
                onClick={() => onEditService(service)}
                style={{ cursor: "pointer", height: "16px" }}
              >
                <EditSvg />
              </div>
            )}
          </div>
          <div style={styles.serviceType(theme)}>
            {enReportServiceType[service.serviceType]}
          </div>
          {description && (
            <div style={styles.serviceDescription(theme)}>{description}</div>
          )}
          <div style={styles.serviceTitleLogicDetails}>
            <div style={styles.serviceTitleLogicDetailsElement}>
              <div style={styles.detailLabel(theme)}>Prescription Period</div>
              <Box sx={styles.periodValue(theme)}>
                <Box
                  component="img"
                  src={calendarIcon}
                  alt="calendar"
                  sx={styles.calendarIcon}
                />
                <Box component="p" sx={styles.periodText(theme)}>
                  {formatPrescriptionPeriod({
                    startDate: service.startDate,
                    endDate: service.endDate,
                    deactivationDate: service.deactivationDate,
                  })}
                </Box>
              </Box>
            </div>
            <div style={styles.serviceTitleLogicDetailsElement}>
              <div style={styles.detailLabel(theme)}>Logic name</div>
              {service.logicName ? (
                <Link
                  style={styles.logicNameContainer(theme)}
                  to={`/client/services/logic/${service.userLogicBuilderID}`}
                  target="_blank"
                >
                  <div style={styles.logicNameIcon}>
                    <LinkSvg />
                  </div>
                  <p>{service.logicName}</p>
                </Link>
              ) : (
                <div style={styles.logicNameNull(theme)}>-</div>
              )}
            </div>
            <div style={styles.serviceTitleLogicDetailsElement}>
              <div style={styles.detailLabel(theme)}>Logic origin</div>
              {service.logicName ? (
                <div style={{ ...styles.detailValue(theme), marginTop: "4px" }}>
                  {service.system === 1 ? "PNOE" : "Custom"}
                </div>
              ) : (
                <div style={styles.logicNameNull(theme)}>-</div>
              )}
            </div>
          </div>
        </div>
        {/*<MoreOptionsPopover*/}
        {/*  options={createServiceMenuOptions()}*/}
        {/*  icon={<MoreIcon fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT} />}*/}
        {/*/>*/}
      </div>
      {/* Prescription details grid */}
      <div style={styles.prescriptionDetails(theme)}>
        <div style={styles.prescriptionDetailsTitle(theme)}>
          Prescription details
        </div>
        <div style={styles.attributesGrid}>
          {service.attributes &&
            service.attributes.length > 0 &&
            service.attributes
              .filter((a: any) => a.kind !== 1)
              .sort((a: any, b: any) => {
                // Define priority order: "Sessions Frequency" first, then "IV Sessions Frequency", then others
                const getPriority = (name: string) => {
                  if (name === "Sessions Frequency") return 0;
                  if (name === "IV Sessions Frequency") return 1;
                  if (name === "Follow-up Frequency") return 2;
                  if (name === "Administration Frequency") return 3;
                  return 4;
                };
                return getPriority(a.name) - getPriority(b.name);
              })
              .map((attribute: any, attrIndex: number) => {
                const displayValue = renderAttributeValue(attribute);

                // Always render, showing "-" if empty
                if (
                  !displayValue ||
                  displayValue === "" ||
                  displayValue === null ||
                  displayValue === undefined
                ) {
                  return (
                    <DetailElement
                      key={attribute.id || attribute.attributeID || attrIndex}
                      label={attribute.name}
                      value="-"
                    />
                  );
                }

                return (
                  <DetailElement
                    key={attribute.id || attribute.attributeID || attrIndex}
                    label={attribute.name}
                    value={displayValue}
                  />
                );
              })}
        </div>
      </div>
      {/* Service information — locations, price, locator visibility — is the
          affiliate's catalog entry, which only the client workspace loads.
          Without it the section is left out: `ServiceInformationDetails`
          renders "Not visible on locator" / "Price not set" for absent
          fields, which would assert things about the service that are not
          true. */}
      {savedService && (
        <div style={styles.serviceInformation}>
          <div style={styles.serviceInformationTitle(theme)}>
            Service information
          </div>
          <ServiceInformationDetails
            service={savedService}
            // The catalog's "expired, but the prescription is still active
            // until …" notice only holds while the prescribed service runs.
            prescriptionEndDate={
              status === "active"
                ? effectiveEndDate({
                    endDate: service.endDate,
                    deactivationDate: service.deactivationDate,
                  }) ?? undefined
                : undefined
            }
          />
        </div>
      )}
      {/* Rationale information */}
      <PrescriptionRationale rules={rules} />
    </div>
  );
};

export default PrescriptionServiceCard;

const styles = {
  serviceCard: (theme: Theme) => ({
    padding: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
  }),
  contentContainerHeader: {
    display: "flex",
    gap: "24px",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  serviceImage: {
    width: "256px",
    height: "122px",
    overflow: "hidden",
    borderRadius: "8px",
  },
  serviceTitleContainer: {
    flex: 1,
  },
  serviceTitleWrapper: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
  },
  serviceTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "28px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    flex: 1,
  }),
  serviceType: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  serviceDescription: (theme: Theme) => ({
    margin: "16px 0",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  serviceTitleLogicDetails: {
    display: "flex",
    justifyContent: "space-between",
    gap: "24px",
  },
  serviceTitleLogicDetailsElement: {
    flex: 1,
  },
  periodValue: (theme: Theme) => ({
    marginTop: "4px",
    backgroundColor: theme.colorTokens.N[30], // #F7F9FA
    borderRadius: "8px",
    padding: "8px 12px",
    display: "flex",
    gap: "8px",
    alignItems: "center",
    width: "fit-content",
  }),
  periodText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT, // #3B3F42
    textAlign: "right" as "right",
    margin: 0,
  }),
  calendarIcon: {
    width: "12px",
    height: "12px",
    objectFit: "contain" as "contain",
  },
  logicNameContainer: (theme: Theme) => ({
    display: "flex",
    padding: "8px 0",
    gap: "8px",
    alignItems: "flex-start",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    width: "fit-content",
    marginTop: "4px",
  }),
  logicNameIcon: {
    marginTop: "2px",
  },
  logicNameNull: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT, // #3B3F42
    marginTop: "4px",
  }),
  serviceBadge: (theme: Theme) => ({
    padding: "4px 12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    borderRadius: "8px",
  }),
  serviceInformation: {
    marginTop: "24px",
  },
  serviceInformationTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    lineHeight: "22px",
    marginBottom: "16px",
  }),
  prescriptionDetails: (theme: Theme) => ({
    margin: "24px 0",
    padding: "24px 0",
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  prescriptionDetailsTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    lineHeight: "22px",
  }),
  attributesGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "16px 24px",
    marginTop: "16px",
  },
  detailElement: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  detailLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  detailValueContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  detailValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};
